/**
 * Meeting Invite Service
 *
 * Handles sending meeting invitations with push notifications.
 * Creates documents in Firestore that trigger Cloud Functions to send notifications.
 */

import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Send a meeting invitation
 *
 * Creates a meetingInvites document that triggers Cloud Function to send push notification.
 *
 * @param {Object} params
 * @param {string} params.fromUserId - Sender's user ID
 * @param {string} params.toUserId - Recipient's user ID
 * @param {string} params.fromUserName - Sender's display name
 * @param {string} params.meetingId - Meeting ID
 * @param {string} [params.meetingName] - Optional meeting name
 * @param {string} [params.accessCode] - Optional access code
 * @param {string} [params.personalNote] - Optional personal note
 * @returns {Promise<{id: string}>} - The invite document ID
 */
export async function sendMeetingInvite({
  fromUserId,
  toUserId,
  fromUserName,
  meetingId,
  meetingName = null,
  accessCode = null,
  personalNote = null
}) {
  const inviteData = {
    fromUserId,
    toUserId,
    fromUserName,
    meetingId,
    meetingName,
    accessCode,
    personalNote,
    status: 'pending', // pending, viewed, accepted, declined
    createdAt: serverTimestamp()
  };

  const inviteRef = await addDoc(collection(db, 'meetingInvites'), inviteData);

  return { id: inviteRef.id, ...inviteData };
}

/**
 * Get meeting join URL
 *
 * @param {string} meetingId - Meeting ID
 * @returns {string} - Full meeting URL
 */
export function getMeetingUrl(meetingId) {
  return `${window.location.origin}/meet/${meetingId}`;
}

export default {
  sendMeetingInvite,
  getMeetingUrl
};
